import { AdminChartArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/chart';
import { CodebaseWikiArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/codebase_wiki';
import { ConsoleOutputArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/console_output';
import { AdminCsvArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/csv';
import { AdminDashboardPageArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/dashboard_page';
import { DiffOutputArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/diff_output';
import { AdminEvidenceBundleArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/evidence_bundle';
import { UnsupportedAdminArtifactRenderer } from '@apps/web/routes/_app/admin/agent/artifacts/fallback';
import {
    AdminFileDownloadArtifact,
    AdminMarkdownArtifact,
} from '@apps/web/routes/_app/admin/agent/artifacts/file';
import { type AdminAgentArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import { AdminImageArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/image';
import { InvestigationLedgerArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/investigation_ledger';
import { OutreachReconciliationArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/outreach_reconciliation';
import { AdminRegistryFixtureArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/registry_fixture';
import {
    type AdminArtifactRendererComponent,
    artifactRegistryKey,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';
import { AdminShiftCalendarArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/shift_calendar';
import { StructuredPayloadArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/structured_payload';
import { TokenBreakdownArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/token_breakdown';
import { AdminToolResultFullArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/tool_result_full';
import { TraceTimelineArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/trace_timeline';
import { UploadedImageArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/uploaded_image';
import { AdminVisualizationArtifact } from '@apps/web/routes/_app/admin/agent/artifacts/visualization';
import artifactRegistryManifest from '@libraries/python/agent_event_framework/admin_artifact_registry.json';

export const ADMIN_ARTIFACT_REGISTRY_KEYS =
    artifactRegistryManifest.artifacts.map(
        ({ artifact_type, artifact_version }) =>
            `${artifact_type}@${artifact_version}`,
    );
export const INLINE_RENDERABLE_ARTIFACT_KEYS = new Set(
    artifactRegistryManifest.artifacts
        .filter(({ inline }) => inline)
        .map(
            ({ artifact_type, artifact_version }) =>
                `${artifact_type}@${artifact_version}`,
        ),
);

export function isInlineRenderableArtifact(
    artifactType: string,
    artifactVersion: number | undefined,
): boolean {
    return (
        typeof artifactVersion === 'number' &&
        INLINE_RENDERABLE_ARTIFACT_KEYS.has(
            `${artifactType}@${artifactVersion}`,
        )
    );
}

export const ADMIN_REGISTRY_FIXTURE_ARTIFACT_KEY =
    'admin.registry_fixture.summary@1';
export const ADMIN_CHART_ARTIFACT_KEY = 'admin.chart@1';
export const ADMIN_CSV_ARTIFACT_KEY = 'admin.csv@1';
export const ADMIN_DASHBOARD_PAGE_ARTIFACT_KEY = 'admin.dashboard_page@1';
export const ADMIN_CODEBASE_WIKI_ARTIFACT_KEY = 'admin.codebase_wiki.atlas@1';
export const ADMIN_EVIDENCE_BUNDLE_ARTIFACT_KEY = 'admin.evidence_bundle@1';
export const ADMIN_IMAGE_ARTIFACT_KEY = 'admin.image@1';
export const ADMIN_FILE_BINARY_ARTIFACT_KEY = 'admin.file.binary@1';
export const ADMIN_FILE_CSV_ARTIFACT_KEY = 'admin.file.csv@1';
export const ADMIN_FILE_HTML_ARTIFACT_KEY = 'admin.file.html@1';
export const ADMIN_FILE_JPEG_ARTIFACT_KEY = 'admin.file.jpeg@1';
export const ADMIN_FILE_JSON_ARTIFACT_KEY = 'admin.file.json@1';
export const ADMIN_FILE_MARKDOWN_ARTIFACT_KEY = 'admin.file.markdown@1';
export const ADMIN_FILE_PDF_ARTIFACT_KEY = 'admin.file.pdf@1';
export const ADMIN_FILE_PNG_ARTIFACT_KEY = 'admin.file.png@1';
export const ADMIN_FILE_TEXT_ARTIFACT_KEY = 'admin.file.text@1';
export const ADMIN_FILE_XLSX_ARTIFACT_KEY = 'admin.file.xlsx@1';
export const ADMIN_FILE_ZIP_ARTIFACT_KEY = 'admin.file.zip@1';
export const ADMIN_INVESTIGATION_LEDGER_ARTIFACT_KEY =
    'admin.investigation_ledger@1';
export const ADMIN_TOKEN_BREAKDOWN_ARTIFACT_KEY = 'admin.token_breakdown@1';
export const ADMIN_TOOL_RESULT_FULL_ARTIFACT_KEY = 'admin.tool_result.full@1';
export const ADMIN_VISUALIZATION_ARTIFACT_KEY = 'admin.visualization@1';
export const ADMIN_SHIFT_CALENDAR_ARTIFACT_KEY = 'admin.shift_calendar@1';
export const ADMIN_OUTREACH_RECONCILIATION_ARTIFACT_KEY =
    'admin.outreach_reconciliation@1';
export const TRACE_TIMELINE_ARTIFACT_KEY = 'TraceTimeline@1';
export const ADMIN_TRACE_TIMELINE_ARTIFACT_KEY = 'admin.trace_timeline@1';
export const ADMIN_AGENT_RUN_TRACE_TIMELINE_ARTIFACT_KEY =
    'admin.agent_run_trace.timeline@1';
// Retained solely so historical admin runs continue to render their stored
// artifact payloads. New tools emit admin.code_sandbox.output instead.
export const LEGACY_PYTHON_SNIPPET_OUTPUT_ARTIFACT_KEY =
    'admin.python_snippet.output@1';
export const ADMIN_CODE_SANDBOX_OUTPUT_ARTIFACT_KEY =
    'admin.code_sandbox.output@1';
export const ADMIN_CODE_SANDBOX_DIFF_ARTIFACT_KEY = 'admin.code_sandbox.diff@1';
export const ADMIN_RUN_DATA_DIFF_OUTPUT_ARTIFACT_KEY =
    'admin.run_data_diff.output@1';
export const ADMIN_USER_UPLOADED_IMAGE_ARTIFACT_KEY =
    'admin.user_uploaded_image@1';

// Every artifact type the backend can emit must resolve to a dedicated
// renderer here — enforced by registry.test.ts. Structured document
// types share the readable StructuredPayloadArtifact view.
const STRUCTURED_PAYLOAD_ARTIFACT_KEYS = [
    // keep-sorted start
    'admin.agent_court_case_raw@1',
    'admin.agent_health_sweep.evidence@1',
    'admin.agent_observability_triage.evidence@1',
    'admin.braintrust_eval_examples@1',
    'admin.call_recording_search_matches@1',
    'admin.call_recording_transcript_page@1',
    'admin.external_api.payload@1',
    'admin.head_justice_digest_preview@1',
    'admin.json_slice.output@1',
    'admin.logfire_http_route_entity_lookup@1',
    'admin.missing_tool.report@1',
    'admin.orchard_code_change_task@1',
    'admin.run_data_read.output@1',
    'admin.run_data_search.output@1',
    'admin.trace@1',
    'core.account_book@1',
    'core.account_dossier@1',
    'core.email_message@1',
    'core.email_thread@1',
    'core.funnel_snapshot@1',
    // keep-sorted end
] as const;

export const adminArtifactRendererRegistry: Record<
    string,
    AdminArtifactRendererComponent
> = {
    [ADMIN_REGISTRY_FIXTURE_ARTIFACT_KEY]: AdminRegistryFixtureArtifact,
    [ADMIN_CHART_ARTIFACT_KEY]: AdminChartArtifact,
    [ADMIN_CSV_ARTIFACT_KEY]: AdminCsvArtifact,
    [ADMIN_DASHBOARD_PAGE_ARTIFACT_KEY]: AdminDashboardPageArtifact,
    [ADMIN_CODEBASE_WIKI_ARTIFACT_KEY]: CodebaseWikiArtifact,
    [ADMIN_EVIDENCE_BUNDLE_ARTIFACT_KEY]: AdminEvidenceBundleArtifact,
    [ADMIN_IMAGE_ARTIFACT_KEY]: AdminImageArtifact,
    [ADMIN_FILE_BINARY_ARTIFACT_KEY]: AdminFileDownloadArtifact,
    [ADMIN_FILE_CSV_ARTIFACT_KEY]: AdminFileDownloadArtifact,
    [ADMIN_FILE_HTML_ARTIFACT_KEY]: AdminFileDownloadArtifact,
    [ADMIN_FILE_JPEG_ARTIFACT_KEY]: AdminFileDownloadArtifact,
    [ADMIN_FILE_JSON_ARTIFACT_KEY]: AdminFileDownloadArtifact,
    [ADMIN_FILE_MARKDOWN_ARTIFACT_KEY]: AdminMarkdownArtifact,
    [ADMIN_FILE_PDF_ARTIFACT_KEY]: AdminFileDownloadArtifact,
    [ADMIN_FILE_PNG_ARTIFACT_KEY]: AdminFileDownloadArtifact,
    [ADMIN_FILE_TEXT_ARTIFACT_KEY]: AdminFileDownloadArtifact,
    [ADMIN_FILE_XLSX_ARTIFACT_KEY]: AdminFileDownloadArtifact,
    [ADMIN_FILE_ZIP_ARTIFACT_KEY]: AdminFileDownloadArtifact,
    [ADMIN_INVESTIGATION_LEDGER_ARTIFACT_KEY]: InvestigationLedgerArtifact,
    [ADMIN_TOKEN_BREAKDOWN_ARTIFACT_KEY]: TokenBreakdownArtifact,
    [ADMIN_TOOL_RESULT_FULL_ARTIFACT_KEY]: AdminToolResultFullArtifact,
    [ADMIN_VISUALIZATION_ARTIFACT_KEY]: AdminVisualizationArtifact,
    [ADMIN_SHIFT_CALENDAR_ARTIFACT_KEY]: AdminShiftCalendarArtifact,
    [ADMIN_OUTREACH_RECONCILIATION_ARTIFACT_KEY]:
        OutreachReconciliationArtifact,
    [TRACE_TIMELINE_ARTIFACT_KEY]: TraceTimelineArtifact,
    [ADMIN_TRACE_TIMELINE_ARTIFACT_KEY]: TraceTimelineArtifact,
    [ADMIN_AGENT_RUN_TRACE_TIMELINE_ARTIFACT_KEY]: TraceTimelineArtifact,
    [LEGACY_PYTHON_SNIPPET_OUTPUT_ARTIFACT_KEY]: ConsoleOutputArtifact,
    [ADMIN_CODE_SANDBOX_OUTPUT_ARTIFACT_KEY]: ConsoleOutputArtifact,
    [ADMIN_CODE_SANDBOX_DIFF_ARTIFACT_KEY]: DiffOutputArtifact,
    [ADMIN_RUN_DATA_DIFF_OUTPUT_ARTIFACT_KEY]: DiffOutputArtifact,
    [ADMIN_USER_UPLOADED_IMAGE_ARTIFACT_KEY]: UploadedImageArtifact,
    ...Object.fromEntries(
        STRUCTURED_PAYLOAD_ARTIFACT_KEYS.map((key) => [
            key,
            StructuredPayloadArtifact,
        ]),
    ),
};

export function resolveAdminArtifactRenderer(
    artifact: AdminAgentArtifact,
): AdminArtifactRendererComponent {
    const registryKey = artifactRegistryKey(artifact);
    return (
        adminArtifactRendererRegistry[registryKey] ??
        UnsupportedAdminArtifactRenderer
    );
}
